import { create } from 'zustand';
import { onRefreshedPrincipal, request, readTokens, writeTokens, type Tokens } from '../lib/api';
import type { Capability, Role, StaffUser } from '../lib/accounts';

export interface Session {
  user: StaffUser;
  signedInAt: string;
  mustChangePassword: boolean;
}

interface StaffPrincipal {
  id: string;
  audience: 'staff';
  name: string;
  email: string;
  role: Role;
  city?: string;
  capabilities?: Capability[];
  jobTitle?: string;
  mustChangePassword?: boolean;
}

type AuthResult = { ok: true } | { ok: false; error: string };

interface AuthState {
  session: Session | null;
  status: 'checking' | 'authenticated' | 'anonymous';
  pending: boolean;
  bootstrap: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
}

function validatePrincipal(principal: StaffPrincipal): StaffPrincipal {
  if (principal.audience !== 'staff' || (principal.role !== 'admin' && principal.role !== 'employee')) {
    throw new Error('This account is not authorised for the operations console.');
  }
  return principal;
}

function toSession(principal: StaffPrincipal): Session {
  return {
    user: {
      id: principal.id,
      name: principal.name,
      email: principal.email,
      role: principal.role,
      city: principal.city || null,
      lastActiveAt: new Date().toISOString(),
      active: true,
      capabilities: principal.capabilities ?? [],
      jobTitle: principal.jobTitle,
    },
    signedInAt: new Date().toISOString(),
    mustChangePassword: Boolean(principal.mustChangePassword),
  };
}

function isStaffPrincipal(value: unknown): value is StaffPrincipal {
  const candidate = value as Partial<StaffPrincipal> | null;
  return Boolean(candidate && candidate.audience === 'staff' && typeof candidate.id === 'string');
}

/** Pages listed in a different order are the same access. */
function sameAccess(a: readonly Capability[] = [], b: readonly Capability[] = []): boolean {
  return a.length === b.length && a.every((capability) => b.includes(capability));
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

let bootstrapPromise: Promise<void> | null = null;

export const useAuth = create<AuthState>((set) => ({
  session: null,
  status: 'checking',
  pending: false,

  bootstrap: async () => {
    if (bootstrapPromise) return bootstrapPromise;
    bootstrapPromise = (async () => {
      if (!readTokens()) {
        set({ status: 'anonymous', session: null, pending: false });
        return;
      }
      try {
        const { data } = await request<StaffPrincipal>('/me');
        const principal = validatePrincipal(data);
        set({ status: 'authenticated', session: toSession(principal), pending: false });
      } catch {
        writeTokens(null);
        set({ status: 'anonymous', session: null, pending: false });
      }
    })().finally(() => {
      bootstrapPromise = null;
    });
    return bootstrapPromise;
  },

  signIn: async (email, password) => {
    set({ pending: true });
    try {
      const { data } = await request<{ tokens: Tokens; principal: StaffPrincipal }>(
        '/admin/auth/login',
        { method: 'POST', auth: false, body: { email: email.trim().toLowerCase(), password } },
      );
      const principal = validatePrincipal(data.principal);
      writeTokens(data.tokens);
      set({
        pending: false,
        status: 'authenticated',
        session: toSession(principal),
      });
      return { ok: true as const };
    } catch (error) {
      writeTokens(null);
      set({ pending: false, status: 'anonymous', session: null });
      return { ok: false, error: errorMessage(error, 'Sign-in failed.') };
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    set({ pending: true });
    try {
      await request('/admin/me/password', {
        method: 'POST',
        body: { currentPassword, newPassword },
        retry: false,
      });
      // The server revokes every refresh session after a password change. Clear
      // this device too and require a clean sign-in with the new credential.
      writeTokens(null);
      set({ pending: false, session: null, status: 'anonymous' });
      return { ok: true as const };
    } catch (error) {
      set({ pending: false });
      return { ok: false, error: errorMessage(error, 'Password change failed.') };
    }
  },

  signOut: async () => {
    const refreshToken = readTokens()?.refreshToken;
    try {
      if (refreshToken) {
        await request('/auth/logout', {
          method: 'POST',
          body: { refreshToken },
          retry: false,
        });
      }
    } catch {
      // Local sign-out must still complete when the API is unavailable.
    }
    writeTokens(null);
    set({ session: null, status: 'anonymous', pending: false });
  },
}));

let accessListener: (() => void) | null = null;

/** Called when the signed-in account's pages change mid-session. */
export function onAccessChanged(listener: (() => void) | null): void {
  accessListener = listener;
}

// A refreshed token carries the account's current pages. When an administrator has
// changed them, the console follows: the navigation and page checks read the session,
// and the data store reloads what the account may now read.
onRefreshedPrincipal((value) => {
  if (!isStaffPrincipal(value)) return;
  const current = useAuth.getState().session;
  if (!current || current.user.id !== value.id) return;
  let principal: StaffPrincipal;
  try {
    principal = validatePrincipal(value);
  } catch {
    return;
  }
  if (principal.role === current.user.role && sameAccess(principal.capabilities, current.user.capabilities)) {
    return;
  }
  const next = toSession(principal);
  useAuth.setState({ session: { ...next, signedInAt: current.signedInAt } });
  accessListener?.();
});

export function useCurrentUser(): StaffUser | undefined {
  return useAuth((state) => state.session?.user);
}

export function useRole(): Role | undefined {
  return useAuth((state) => state.session?.user.role);
}
